import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TwoFactorAuth = ({ onToggle2FA }) => {
  const [step, setStep] = useState('setup'); // setup, verify, success
  const [verificationCode, setVerificationCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Generate mock QR code and secret key
    const mockSecret = 'JBSWY3DPEHPK3PXP';
    const mockQRUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=otpauth://totp/VINPro:usuario@vinpro.mx?secret=${mockSecret}&issuer=VINPro`;
    
    setSecretKey(mockSecret);
    setQrCode(mockQRUrl);
  }, []);

  const handleVerifyCode = async (e) => {
    e?.preventDefault();
    
    if (!verificationCode || verificationCode?.length !== 6) {
      setErrors({ code: 'Ingrese un código de 6 dígitos válido' });
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate verification delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock verification - accept 123456 as valid code
      if (verificationCode === '123456') {
        setStep('success');
        setErrors({});
        
        // Store 2FA status
        localStorage.setItem('vinpro_2fa_enabled', 'true');
        
        setTimeout(() => {
          onToggle2FA();
        }, 2000);
      } else {
        setErrors({ code: 'Código inválido. Use: 123456 para pruebas' });
      }
    } catch (error) {
      setErrors({ code: 'Error de verificación. Intente nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e?.target?.value?.replace(/\D/g, '')?.slice(0, 6);
    setVerificationCode(value);
    
    if (errors?.code) {
      setErrors({});
    }
  };

  if (step === 'success') {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-card border border-border rounded-lg shadow-elevation p-8">
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-success rounded-full mx-auto mb-4">
              <Icon name="Check" size={32} color="white" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              2FA Configurado Exitosamente
            </h2>
            <p className="text-muted-foreground mb-6">
              Su cuenta ahora está protegida con autenticación de dos factores
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-success">
              <Icon name="Shield" size={16} />
              <span>Seguridad mejorada activada</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-elevation p-8">
        <div className="text-center mb-6">
          <Icon name="Shield" size={48} className="text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-foreground">
            Configurar Autenticación 2FA
          </h2>
          <p className="text-muted-foreground mt-2">
            Mejore la seguridad de su cuenta con verificación en dos pasos
          </p>
        </div>

        {step === 'setup' && (
          <div className="space-y-6">
            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-3">Paso 1: Escanear Código QR</h3>
              <div className="text-center">
                <div className="bg-white p-4 rounded-lg inline-block mb-4">
                  <img
                    src={qrCode}
                    alt="Código QR para configuración de autenticación de dos factores"
                    className="w-48 h-48 mx-auto"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Escanee este código con su aplicación de autenticación
                </p>
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-3">Paso 2: Clave Manual</h3>
              <div className="bg-card border border-border rounded p-3">
                <code className="text-sm font-mono text-foreground break-all">
                  {secretKey}
                </code>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Use esta clave si no puede escanear el código QR
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-medium text-foreground">Aplicaciones Recomendadas:</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Smartphone" size={16} />
                  <span>Google Authenticator</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Smartphone" size={16} />
                  <span>Microsoft Authenticator</span>
                </div>
              </div>
            </div>

            <Button
              variant="default"
              fullWidth
              onClick={() => setStep('verify')}
              iconName="ArrowRight"
              iconPosition="right"
            >
              Continuar a Verificación
            </Button>

            <Button
              variant="ghost"
              fullWidth
              onClick={onToggle2FA}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Volver al Inicio de Sesión
            </Button>
          </div>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerifyCode} className="space-y-6">
            <div className="text-center">
              <h3 className="font-medium text-foreground mb-2">
                Verificar Configuración
              </h3>
              <p className="text-sm text-muted-foreground">
                Ingrese el código de 6 dígitos de su aplicación de autenticación
              </p>
            </div>

            <Input
              label="Código de Verificación"
              type="text"
              placeholder="123456"
              value={verificationCode}
              onChange={handleCodeChange}
              error={errors?.code}
              maxLength={6}
              className="text-center text-2xl font-mono tracking-widest"
              required
            />

            <div className="bg-warning/10 border border-warning/20 rounded-md p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-warning mt-0.5" />
                <div className="text-sm">
                  <p className="text-warning font-medium">Para pruebas:</p>
                  <p className="text-muted-foreground">Use el código: <code>123456</code></p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={isLoading}
                iconName="Shield"
                iconPosition="left"
              >
                Verificar y Activar 2FA
              </Button>

              <Button
                type="button"
                variant="ghost"
                fullWidth
                onClick={() => setStep('setup')}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Volver a Configuración
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TwoFactorAuth;